import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './TrainingCompletedModal.module.scss';

export interface TrainingCompletedModalProps {
    open: boolean;
    onClose: () => void;
    title?: string | React.ReactNode;
    description?: string | React.ReactNode;
    extraText?: string | React.ReactNode;
    buttonLabel?: string | React.ReactNode;
    children?: React.ReactNode;
    position?: 'center' | 'bottom-right' | 'top-right';
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    showCheckbox?: boolean;
    checkboxLabel?: string;
    checkboxChecked?: boolean;
    onCheckboxChange?: (checked: boolean) => void;
    onButtonClickWithCheckbox?: (checkboxChecked: boolean) => void;
    extraContainerStyle?: React.CSSProperties;
    checkboxContainerStyle?: React.CSSProperties;
}

const DEFAULT_PROPS = {
    title: 'Título do modal',
    description: 'Descrição do modal',
    extraText: '',
    buttonLabel: 'Fechar',
    position: 'bottom-right' as const,
    closeOnOverlayClick: true,
    closeOnEscape: true,
    ariaLabel: 'Modal dialog',
};

const TrainingCompletedModal: React.FC<TrainingCompletedModalProps> = (props) => {
    const {
        open,
        onClose,
        title = DEFAULT_PROPS.title,
        description = DEFAULT_PROPS.description,
        extraText = DEFAULT_PROPS.extraText,
        buttonLabel = DEFAULT_PROPS.buttonLabel,
        children,
        position = DEFAULT_PROPS.position,
        closeOnOverlayClick = DEFAULT_PROPS.closeOnOverlayClick,
        closeOnEscape = DEFAULT_PROPS.closeOnEscape,
        ariaLabel = DEFAULT_PROPS.ariaLabel,
        extraContainerStyle = {},
        ariaDescribedBy,
        showCheckbox = false,
        checkboxLabel = 'Não mostrar esta notificação novamente',
        checkboxChecked = false,
        onCheckboxChange,
        onButtonClickWithCheckbox,
        checkboxContainerStyle = {},
    } = props;

    const [isMobile, setIsMobile] = useState(false);
    const [internalCheckboxChecked, setInternalCheckboxChecked] = useState(checkboxChecked);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            setInternalCheckboxChecked(checked);
            if (onCheckboxChange) {
                onCheckboxChange(checked);
            }
        },
        [onCheckboxChange],
    );

    const handleButtonClick = useCallback(() => {
        if (onButtonClickWithCheckbox) {
            onButtonClickWithCheckbox(internalCheckboxChecked);
        } else {
            onClose();
        }
    }, [onButtonClickWithCheckbox, internalCheckboxChecked, onClose]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (closeOnEscape && event.key === 'Escape') {
                onClose();
            }
        },
        [closeOnEscape, onClose],
    );

    const handleOverlayClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (closeOnOverlayClick && event.target === event.currentTarget) {
                onClose();
            }
        },
        [closeOnOverlayClick, onClose],
    );

    const handleTouchStart = useCallback(
        (event: React.TouchEvent) => {
            if (!isMobile) return;

            const touch = event.touches[0];
            const startY = touch.clientY;

            const handleTouchMove = (moveEvent: TouchEvent) => {
                const currentTouch = moveEvent.touches[0];
                const deltaY = currentTouch.clientY - startY;

                if (deltaY > 100) {
                    onClose();
                    document.removeEventListener('touchmove', handleTouchMove);
                    document.removeEventListener('touchend', handleTouchEnd);
                }
            };

            const handleTouchEnd = () => {
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };

            document.addEventListener('touchmove', handleTouchMove, { passive: true });
            document.addEventListener('touchend', handleTouchEnd);
        },
        [isMobile, onClose],
    );

    useEffect(() => {
        if (open && closeOnEscape) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [open, closeOnEscape, handleKeyDown]);

    useEffect(() => {
        if (open) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [open]);

    if (!open) return null;

    const stylePosition = {
        center: {
            overlay: styles.overlay,
            modal: styles.modal,
        },
        'bottom-right': {
            overlay: styles.overlayNoCenter,
            modal: styles.modalBottomRight,
        },
        'top-right': {
            overlay: styles.overlayTopRight,
            modal: styles.modalTopRight,
        },
    }

    const modalContent = (
        <>
            <div className={styles.HitModalTrainingCompletedModal} onClick={onClose}></div>
            <div className={styles.TrainingCompletedModal}>
                <div className={stylePosition[position].overlay} onClick={handleOverlayClick}>
                    <div
                        style={extraContainerStyle}
                        className={stylePosition[position].modal}
                        role="dialog"
                        aria-modal="true"
                        aria-label={ariaLabel}
                        aria-describedby={ariaDescribedBy}
                        onTouchStart={handleTouchStart}
                    >
                        <div>{title}</div>
                        {description && (
                            <div className={styles.text} id={ariaDescribedBy || 'modal-description'}>
                                {description}
                            </div>
                        )}
                        {extraText && <div className={styles.text}>{extraText}</div>}
                        {children}
                        {showCheckbox && (
                            <div className={styles.checkboxContainer} style={checkboxContainerStyle}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={internalCheckboxChecked}
                                        onChange={handleCheckboxChange}
                                        className={styles.checkbox}
                                    />
                                    <span className={styles.checkboxText}>{checkboxLabel}</span>
                                </label>
                            </div>
                        )}
                        <button
                            className={styles.button}
                            onClick={handleButtonClick}
                            type="button"
                            aria-label={typeof buttonLabel === 'string' ? buttonLabel : 'Fechar modal'}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );

    return createPortal(modalContent, document.body);
};

export default TrainingCompletedModal;
